import { createFileRoute } from '@tanstack/react-router'
import { Map as GoogleMap, useMap } from '@vis.gl/react-google-maps'
import { useEffect, useMemo, useState, type ComponentProps } from 'react'
import { useGoogleMapsColorScheme } from '@/shared/providers/useGoogleMapsColorScheme'
import { ChevronLeft, ChevronRight, PanelLeftOpen, PanelRightOpen } from 'lucide-react'
import { useQueries } from '@tanstack/react-query'
import {
  useLiveMapFeed,
  useGuardLocationHistory,
} from '@entities/guard-location'
import { useAllSitesLookup } from '@/entities/site/model/queries'
import { fetchSiteById } from '@/entities/site/api/siteApi'
import type { Site } from '@/entities/site/model/types'
import { useGuardLookup } from '@/entities/employee/model/queries'
import { SOS_GUARD_SUBTYPE_CODE, type IncidentLocation } from '@/entities/incident'
import { usePermissions } from '@/entities/session/hooks/usePermissions'
import { PERMISSIONS } from '@/shared/config/permissions'
import {
  GuardMapMarker,
  GuardMemoPeekSheet,
  GuardTrail,
  GuardListPanel,
  MapFitToBounds,
  MapTypeToggle,
  PositionsDegradedBanner,
  SiteLabeledPin,
  SiteZonesLayer,
  IncidentListPanel,
  IncidentMapMarker,
  useOpenBubble,
  type MapTypeId,
} from '@features/guard-location-map'
import {
  deriveOnDutySites,
  guardPositionsById,
  incidentMarkerPosition,
  isBubbleFor,
  liveGuardRowKey,
  resolveGuardName,
  sosIncidentsByGuard,
} from '@/features/guard-location-map/liveMapHelpers'
import { SuspendAccessDialog } from '@/features/employee-profile/components/SuspendAccessDialog'

const ONE_HOUR_MS = 60 * 60 * 1000

export const Route = createFileRoute('/live-map/')({
  component: LiveMapPage,
  staticData: { authorize: true, fullBleed: true },
})

function LiveMapPage() {
  const colorScheme = useGoogleMapsColorScheme()
  const [selectedGuardUserId, setSelectedGuardUserId] = useState<number | null>(null)
  const [focusedSiteId, setFocusedSiteId] = useState<number | null>(null)
  const [mapTypeId, setMapTypeId] = useState<MapTypeId>('roadmap')
  const [leftMinimized, setLeftMinimized] = useState(false)
  const [rightMinimized, setRightMinimized] = useState(false)
  const [selectedIncidentId, setSelectedIncidentId] = useState<number | null>(null)
  const { openBubble, setOpenBubble } = useOpenBubble()
  const [suspendTarget, setSuspendTarget] = useState<{ guardUserId: number; guardName: string } | null>(null)
  const [memoPeek, setMemoPeek] = useState<{
    guardUserId: number
    guardName: string
    shiftId: number
  } | null>(null)

  // Asking for incidents without the permission would 403 the guard dots too.
  const { hasPermission } = usePermissions()
  const canReviewIncidents = hasPermission(PERMISSIONS.IncidentsReview)
  // Recomputed hourly, not per render: this value is part of the query key.
  const hourBucket = Math.floor(Date.now() / ONE_HOUR_MS)
  const incidentsFrom = useMemo(() => {
    const start = new Date()
    start.setHours(0, 0, 0, 0)
    return start.toISOString()
  }, [hourBucket])

  const feedQuery = useLiveMapFeed({ withIncidents: canReviewIncidents, incidentsFrom })
  const sitesQuery = useAllSitesLookup(true)
  const guardLookupQuery = useGuardLookup()

  const guards = useMemo(() => feedQuery.data?.guards ?? [], [feedQuery.data])
  const positionsDegraded = feedQuery.data?.positionsDegraded ?? false
  const sites = useMemo(() => sitesQuery.data ?? [], [sitesQuery.data])
  const incidents = useMemo(() => feedQuery.data?.incidents ?? [], [feedQuery.data])

  const guardPositions = useMemo(() => guardPositionsById(guards), [guards])
  const sosByGuard = useMemo(
    () => sosIncidentsByGuard(incidents, guardPositions),
    [incidents, guardPositions],
  )
  // A resolved incident that draws like a live one is the wrong ambiguity to leave on a map
  // someone scans during an emergency: a cancelled false alarm would keep a red pin all day.
  const placedIncidents = useMemo(
    () =>
      incidents
        .filter(incident => incident.status !== 'Resolved')
        .filter(incident => sosByGuard.get(incident.guardUserId ?? -1)?.id !== incident.id)
        .map(incident => ({ incident, position: incidentMarkerPosition(incident, guardPositions) }))
        .filter((entry): entry is { incident: IncidentLocation; position: { latitude: number; longitude: number } } =>
          entry.position != null),
    [incidents, guardPositions, sosByGuard],
  )

  const openGuardSos = useMemo(
    () =>
      incidents.filter(
        i =>
          i.status !== 'Resolved' &&
          i.subtypeCode === SOS_GUARD_SUBTYPE_CODE &&
          i.guardUserId != null,
      ),
    [incidents],
  )

  const sosByGuardUserId = useMemo(() => {
    const map = new Map<number, 'unowned' | 'owned'>()
    for (const incident of openGuardSos) {
      const state = incident.acknowledgedAt == null ? 'unowned' : 'owned'
      if (state === 'unowned' || !map.has(incident.guardUserId!)) {
        map.set(incident.guardUserId!, state)
      }
    }
    return map
  }, [openGuardSos])

  const unansweredSosCount = useMemo(
    () => openGuardSos.filter(i => i.acknowledgedAt == null).length,
    [openGuardSos],
  )

  const onDutySites = useMemo(
    () => deriveOnDutySites(guards, sites, incidents),
    [guards, sites, incidents],
  )

  const siteNameById = useMemo(() => {
    const map: Record<number, string> = {}
    for (const s of sites) map[s.siteId] = s.clientName ? `${s.clientName} — ${s.siteName}` : s.siteName
    return map
  }, [sites])

  const guardNameById = useMemo(() => {
    const map: Record<number, string> = {}
    for (const g of guardLookupQuery.data ?? []) map[g.id] = formatGuardName(g)
    return map
  }, [guardLookupQuery.data])

  const resolvedGuardNameById = useMemo(() => {
    const map: Record<number, string> = {}
    for (const g of guards) map[g.guardUserId] = resolveGuardName(g.guardName, guardNameById[g.guardUserId])
    return map
  }, [guards, guardNameById])

  const selectedGuard = useMemo(
    () => guards.find(g => g.guardUserId === selectedGuardUserId) ?? null,
    [guards, selectedGuardUserId],
  )

  const isFocusMode = selectedGuard != null && selectedGuard.latitude != null && selectedGuard.shiftId != null

  const history = useGuardLocationHistory(
    selectedGuard?.shiftId ?? 0,
    isFocusMode ? selectedGuardUserId : null,
  )

  const trailPings = useMemo(
    () => (history.data ?? []).filter(p => p.latitude != null && p.longitude != null),
    [history.data],
  )

  const visibleGuards = useMemo(() => {
    const located = guards.filter(g => g.latitude != null && g.longitude != null)
    if (!isFocusMode || !selectedGuard) return located
    return located.filter(g => g.guardUserId === selectedGuard.guardUserId)
  }, [guards, isFocusMode, selectedGuard])

  const boundsPings = useMemo<ComponentProps<typeof MapFitToBounds>['pings']>(() => {
    if (isFocusMode && selectedGuard) {
      if (trailPings.length > 0) return [selectedGuard, ...trailPings]
      return [selectedGuard]
    }
    return guards
  }, [isFocusMode, selectedGuard, trailPings, guards])

  const boundsSites = isFocusMode ? [] : onDutySites

  const onDutySiteIds = useMemo(
    () => [...new Set(onDutySites.map(s => s.id))],
    [onDutySites],
  )

  const siteDetailQueries = useQueries({
    queries: onDutySiteIds.map(id => ({
      queryKey: ['site', id],
      queryFn: () => fetchSiteById(id),
      enabled: !!id,
      staleTime: 60_000,
    })),
  })

  const fullSites = useMemo(
    () => siteDetailQueries.map(q => q.data).filter((s): s is Site => s != null),
    [siteDetailQueries],
  )

  return (
    <div className="relative h-full overflow-hidden">
      <div className="absolute inset-0">
        <MapTypeToggle value={mapTypeId} onChange={setMapTypeId} />
        <GoogleMap
          defaultCenter={{ lat: 20, lng: 0 }}
          defaultZoom={2}
          mapId="3d65a10ef31b8d027d261ab8"
          mapTypeId={mapTypeId}
          mapTypeControl={false}
          className="h-full w-full"
          colorScheme={colorScheme}
        >
          <MapFitToBounds
            pings={boundsPings}
            sites={boundsSites}
            enabled={!focusedSiteId && selectedIncidentId == null}
          />
          <FocusOnSite siteId={focusedSiteId} sites={onDutySites} />
          <FocusOnIncident incidentId={selectedIncidentId} placed={placedIncidents} />
          {fullSites.map(site => (
            <SiteZonesLayer key={site.siteId} site={site} />
          ))}
          {!isFocusMode && onDutySites.map(s => (
            <SiteLabeledPin
              key={s.id}
              site={s}
              onClick={() => setFocusedSiteId(s.id)}
            />
          ))}
          {visibleGuards.map(g => (
            <GuardMapMarker
              key={liveGuardRowKey(g)}
              ping={g}
              guardName={resolvedGuardNameById[g.guardUserId]}
              isSelected={selectedGuardUserId === g.guardUserId}
              sosIncident={sosByGuard.get(g.guardUserId)}
              onClick={() => handleSelect(g.guardUserId)}
              isBubbleOpen={isBubbleFor(openBubble, 'guard', g.guardUserId)}
              onBubbleOpen={() => setOpenBubble({ kind: 'guard', id: g.guardUserId })}
              onBubbleClose={() => setOpenBubble(null)}
            />
          ))}
          {trailPings.length > 0 && (
            <GuardTrail pings={trailPings} dashed />
          )}
          {!isFocusMode && placedIncidents.map(({ incident, position }) => (
            <IncidentMapMarker
              key={incident.id}
              incident={incident}
              position={position}
              siteName={incident.siteId != null ? siteNameById[incident.siteId] : undefined}
              isSelected={selectedIncidentId === incident.id}
              onClick={() => handleSelectIncident(incident.id)}
              isBubbleOpen={isBubbleFor(openBubble, 'incident', incident.id)}
              onBubbleOpen={() => setOpenBubble({ kind: 'incident', id: incident.id })}
              onBubbleClose={() => setOpenBubble(null)}
            />
          ))}
        </GoogleMap>
      </div>

      {feedQuery.isError && (
        <div className="pointer-events-none absolute left-1/2 top-3 z-20 max-w-md -translate-x-1/2 rounded-md border border-red-300 bg-red-50/95 px-3 py-2 text-xs text-red-900 shadow-lg backdrop-blur-md dark:border-red-700 dark:bg-red-950/80 dark:text-red-200">
          Live data is unavailable. This is not a report that nobody is on duty.
        </div>
      )}

      {positionsDegraded && (
        <PositionsDegradedBanner className="pointer-events-none absolute left-1/2 top-3 z-20 max-w-md -translate-x-1/2 bg-amber-50/95 shadow-lg backdrop-blur-md dark:bg-amber-950/80" />
      )}

      {leftMinimized ? (
        <button
          type="button"
          onClick={() => setLeftMinimized(false)}
          aria-label="Show guards panel"
          className="absolute left-3 top-14 z-10 flex h-10 w-10 items-center justify-center rounded-md bg-surface/90 text-ink-2 shadow-md ring-1 ring-black/5 backdrop-blur-md transition-colors hover:bg-surface hover:text-ink"
        >
          <PanelLeftOpen size={18} />
        </button>
      ) : (
        <div className="pointer-events-none absolute inset-y-4 left-4 z-10 w-80">
          <div className="pointer-events-auto relative h-full overflow-hidden rounded-2xl bg-surface/90 shadow-2xl ring-1 ring-black/10 backdrop-blur-md">
            <button
              type="button"
              onClick={() => setLeftMinimized(true)}
              aria-label="Minimize guards panel"
              className="absolute right-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-md text-ink-3 transition-colors hover:bg-hover hover:text-ink-2"
            >
              <ChevronLeft size={16} />
            </button>
            <GuardListPanel
              guards={guards}
              positionsDegraded={positionsDegraded}
              getGuardName={id => resolvedGuardNameById[id]}
              getSiteName={id => siteNameById[id]}
              selectedGuardUserId={selectedGuardUserId}
              onSelect={handleSelect}
              uniqueSiteCount={onDutySites.length}
              onOpenMemo={setMemoPeek}
              onSuspend={setSuspendTarget}
              sosByGuardUserId={sosByGuardUserId}
              unansweredSosCount={unansweredSosCount}
            />
          </div>
        </div>
      )}

      {rightMinimized ? (
        <button
          type="button"
          onClick={() => setRightMinimized(false)}
          aria-label="Show incidents panel"
          className="absolute right-3 top-14 z-10 flex h-10 w-10 items-center justify-center rounded-md bg-surface/90 text-ink-2 shadow-md ring-1 ring-black/5 backdrop-blur-md transition-colors hover:bg-surface hover:text-ink"
        >
          <PanelRightOpen size={18} />
        </button>
      ) : (
        <div className="pointer-events-none absolute inset-y-4 right-4 z-10 w-80">
          <div className="pointer-events-auto relative h-full overflow-hidden rounded-2xl bg-surface/90 shadow-2xl ring-1 ring-black/10 backdrop-blur-md">
            <button
              type="button"
              onClick={() => setRightMinimized(true)}
              aria-label="Minimize incidents panel"
              className="absolute right-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-md text-ink-3 transition-colors hover:bg-hover hover:text-ink-2"
            >
              <ChevronRight size={16} />
            </button>
            <IncidentListPanel
              incidents={incidents}
              truncated={feedQuery.data?.incidentsTruncated ?? false}
              getSiteName={id => siteNameById[id]}
              getGuardName={id => resolvedGuardNameById[id]}
              selectedIncidentId={selectedIncidentId}
              onSelect={handleSelectIncident}
              uniqueSiteCount={new Set(incidents.map(i => i.siteId).filter(id => id != null)).size}
            />
          </div>
        </div>
      )}

      {suspendTarget && (
        <SuspendAccessDialog
          employee={{ id: suspendTarget.guardUserId, name: suspendTarget.guardName }}
          onClose={() => setSuspendTarget(null)}
        />
      )}

      <GuardMemoPeekSheet
        guardUserId={memoPeek?.guardUserId ?? 0}
        guardName={memoPeek?.guardName ?? ''}
        shiftId={memoPeek?.shiftId ?? 0}
        open={!!memoPeek}
        onOpenChange={(o) => {
          if (!o) setMemoPeek(null)
        }}
      />
    </div>
  )

  function handleSelect(guardUserId: number) {
    setSelectedIncidentId(null)
    setSelectedGuardUserId(prev => {
      const next = prev === guardUserId ? null : guardUserId
      const guard = guards.find(g => g.guardUserId === next)
      if (next != null && guard && guard.status === 'NoSignal') {
        setFocusedSiteId(guard.siteId)
        return null
      }
      setFocusedSiteId(null)
      return next
    })
  }

  function handleSelectIncident(incidentId: number) {
    setSelectedIncidentId(prev => (prev === incidentId ? null : incidentId))
    setSelectedGuardUserId(null)
    setFocusedSiteId(null)
  }
}

function FocusOnSite({ siteId, sites }: { siteId: number | null; sites: ReturnType<typeof deriveOnDutySites> }) {
  const map = useMap()
  useEffect(() => {
    if (!map || siteId == null) return
    const site = sites.find(s => s.id === siteId)
    if (!site) return
    map.panTo({ lat: site.latitude, lng: site.longitude })
    map.setZoom(Math.max(map.getZoom() ?? 14, 16))
  }, [map, siteId, sites])
  return null
}

function FocusOnIncident({
  incidentId,
  placed,
}: {
  incidentId: number | null
  placed: { incident: IncidentLocation; position: { latitude: number; longitude: number } }[]
}) {
  const map = useMap()
  useEffect(() => {
    if (!map || incidentId == null) return
    const entry = placed.find(p => p.incident.id === incidentId)
    if (!entry) return
    map.panTo({ lat: entry.position.latitude, lng: entry.position.longitude })
    map.setZoom(Math.max(map.getZoom() ?? 14, 16))
  }, [map, incidentId, placed])
  return null
}

function formatGuardName(guard: { firstName?: string; lastName?: string; fullName?: string }): string {
  if (guard.fullName) return guard.fullName
  const fn = guard.firstName ?? ''
  const ln = guard.lastName ?? ''
  return `${fn} ${ln}`.trim() || '—'
}
